import { type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type CheckboxOptionType, Col, Radio, Row, theme } from 'antd';
import { PosFormItem } from 'pos-client/components/shared/Form/PosFormItem.tsx';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import { useCountryStaticConfigData } from 'pos-client/hooks/useStaticConfig.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { LocationCreateEditModel } from 'pos-shared/models/locationSchema.ts';

export const LocationSettingsTab = ({
  locationForm,
}: {
  locationForm: UseFormReturn<LocationCreateEditModel>;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const { countryOptions } = useCountryStaticConfigData();
  const countryName = locationForm.getValues()?.address?.country;
  const countryCode = countryOptions?.find(
    (country) => country.name === countryName
  );

  const { data: timeZones, isLoading } =
    trpc.serviceLocation.getTimeZones.useQuery(
      { countryCode: countryCode?.code ?? '' },
      {
        enabled: !!countryCode,
      }
    );

  const { data: categoriesData, isLoading: isLoadingCategories } =
    trpc.locations.getLocationCategories.useQuery({
      resultsPerPage: 250,
    });

  const categoryOptions = (categoriesData?.results ?? []).map((category) => ({
    label: category.name,
    value: category.id,
  }));

  const locationActiveOptions: CheckboxOptionType[] = [
    {
      label: t('statuses.active'),
      value: true,
    },
    {
      label: t('statuses.inactive'),
      value: false,
    },
  ];

  return (
    <>
      <Row gutter={[token.marginSM, 0]}>
        <Col span={12}>
          <PosFormItem
            control={locationForm.control}
            name="isActive"
            label={t('common.status.status')}
            required
          >
            <Radio.Group options={locationActiveOptions} optionType="button" />
          </PosFormItem>
        </Col>
      </Row>
      <Row gutter={[token.marginSM, 0]}>
        <Col span={12}>
          <PosSelect
            control={locationForm.control}
            name="categoryId"
            label={t('categories.categories')}
            placeholder={t('common.buttons.select')}
            showSearch
            loading={isLoadingCategories}
            options={categoryOptions}
            allowClear
          />
        </Col>
      </Row>
      <Row gutter={[token.marginSM, 0]}>
        <Col span={12}>
          <PosSelect
            control={locationForm.control}
            name="serviceTypes"
            label={t('scheduling.serviceType')}
            placeholder={t('common.buttons.select')}
            options={[
              { name: 'Static', value: 'Static' },
              { name: 'Mobile', value: 'Mobile' },
            ]}
            mode="multiple"
          />
        </Col>
      </Row>
      <Row gutter={[token.marginSM, 0]}>
        <Col span={12}>
          <PosSelect
            control={locationForm.control}
            name="address.timeZone"
            label={t('common.time.timezone')}
            showSearch
            loading={isLoading}
            options={timeZones?.map((tz) => ({
              value: tz.zoneId,
              label: tz.displayName,
            }))}
          />
        </Col>
      </Row>
    </>
  );
};
